import React, { FC, ReactNode, useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { FieldErrors, useForm } from 'react-hook-form'
import { Tooltip } from '@mui/material'

import { selectedEvent, selectEventLoading } from '../../../../store/events/eventsSlice'
import { useAppSelector } from '../../../../store/hooks'
import EventFormContainerComponent from '../container/EventFormContainerComponent'

//components
import {
  IEventPaymentSettingsInterface,
  IPaymentOption,
} from '../../../../models/event-payment-settings/event-payment-settings.interface'
import EventPaymentSettingsModel from '../../../../models/event-payment-settings/event-payment-settings.model'
import EventFormFooterCommonComponent from '../../../common/buttons/EventFormFooterCommonComponent'
import ViewsLoader from '../../../loader/ViewsLoader'
import EventFormHeaderComponent from '../header/EventFormHeaderComponent'
import SwitchElementUpdated from '../paperwork/SwitchElement'
import { paymentOptions } from './data/options'
import TooltipIcon from '../../../../helpers/TooltipIcon'
import { cn } from '../../../../lib/utils'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select'

import { currencies } from '../../../../fakeData/currencies'

const EventPaymentSettingsDefaultValues = new EventPaymentSettingsModel().toObject()
const eventReviewPublishFormValidationSchema = EventPaymentSettingsModel.validationSchema()

interface EventPaymentRowProps {
  className?: string
  titleClassName?: string
  title: string
  subtitle?: string
  tooltip?: string
  children?: ReactNode
}
const EventPaymentRow: FC<EventPaymentRowProps> = ({
  className,
  titleClassName,
  title,
  subtitle,
  tooltip,
  children,
}) => {
  return (
    <div
      className={cn('flex gap-2 flex-row items-center justify-between py-3 flex-wrap', className)}
    >
      <div className="flex-grow max-w-[85%]">
        <div
          className={cn('text-xl font-semibold text-SeabiscuitDark200ThemeColor', titleClassName)}
        >
          {title}
        </div>
        {subtitle ? (
          <div className="text-base mt-1 text-SeabiscuitDark200ThemeColor/50 flex">
            {subtitle}
            {tooltip && (
              <Tooltip
                title={<p className="tooltip_title">{tooltip}</p>}
                placement="top"
                arrow
                className="shrink-0"
              >
                <button>
                  <TooltipIcon color="#122B46" />
                </button>
              </Tooltip>
            )}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  )
}
interface EventReviewPublishFormComponentProps {
  onInvalidAndExit: () => void
  data: IEventPaymentSettingsInterface | null
  validFormHandler: (data: IEventPaymentSettingsInterface) => Promise<{
    onSetEventTab: (tab: string) => void
    updated: boolean
  }>
  invalidFormHandler: (errors: FieldErrors<IEventPaymentSettingsInterface>) => void
  loading: boolean
  showErrors: boolean
  nextEventTab?: string
  eventTab?: string
}

const EventPaymentFormComponent: React.FC<EventReviewPublishFormComponentProps> = (props) => {
  const loading = useAppSelector(selectEventLoading)
  const selectedEventR = useAppSelector(selectedEvent)

  const { handleSubmit, trigger, reset, watch, setValue } = useForm({
    defaultValues: EventPaymentSettingsDefaultValues,
    resolver: yupResolver(eventReviewPublishFormValidationSchema),
    mode: 'onChange',
  })

  useEffect(() => {
    if (props.data) {
      reset(props.data)
    } else {
      reset(EventPaymentSettingsDefaultValues)
    }
  }, [props.data, reset, trigger])

  // Functions
  const onTogglePaymentOption = (keyToToggle: IPaymentOption) => {
    if (keyToToggle === watch('paymentOption')) {
      if (watch('paymentOption') === keyToToggle) setValue('paymentOption', null)
    } else {
      setValue('paymentOption', keyToToggle)
    }
    trigger().then()
  }

  const handleToggleClick = (
    name:
      | 'chargeSalesTax'
      | 'allowWithoutPayment'
      | 'allowCreditCardPayment'
      | 'allowACHPayment'
      | 'allowSplitPayment'
      | 'allowDelayedPayment'
  ) => {
    setValue(name, !watch(name))
    trigger().then()
  }

  return (
    <EventFormContainerComponent>
      {loading || props.loading ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <ViewsLoader
            className="flex items-center w-full justify-center min-h-[30vh] mt-3"
            size="lg"
            color="red"
          />
        </div>
      ) : (
        <div className="mr-4 mb-4 w-full mx-auto">
          <EventFormHeaderComponent
            title="Payments"
            description="Pegasus charges 5% on all sales. Select how you would like to manage this fee."
          >
            <EventFormFooterCommonComponent
              eventTab={props.eventTab}
              nextEventTab={props.nextEventTab}
              publishButton={false}
              onSaveAndExit={(e, publishEvent) => {
                handleSubmit(
                  (data) =>
                    publishEvent({
                      dataToSave: data,
                      onSaveAndExit: true,
                      tabName: 'EventPaymentSettings',
                      validFormHandler: props.validFormHandler,
                    }),
                  props.onInvalidAndExit
                )(e)
              }}
              onNext={(e, publishEvent) => {
                handleSubmit(
                  (data) =>
                    publishEvent({
                      dataToSave: data,
                      tabName: 'EventPaymentSettings',
                      validFormHandler: props.validFormHandler,
                    }),
                  props.invalidFormHandler
                )(e)
              }}
            />
          </EventFormHeaderComponent>

          <div className="flex flex-col">
            <EventPaymentRow
              title="Currency"
              subtitle="Currency change will not be available after publication"
            >
              <Select
                disabled={selectedEventR?.Event?.isPublished}
                value={watch('currency').toUpperCase()}
                onValueChange={(value) => {
                  setValue('currency', value.toUpperCase() as keyof typeof currencies)
                  trigger().then()
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.keys(currencies).map((currency) => (
                    <SelectItem key={currency} value={currency.toUpperCase()}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </EventPaymentRow>
            <hr className="my-4" />
            <EventPaymentRow title="Pricing" subtitle="Select a pricing option for this event" />
            {paymentOptions.map((option, index) => {
              return (
                <EventPaymentRow
                  titleClassName="text-lg font-normal"
                  key={index}
                  title={option['title']}
                  subtitle={option['subtitle']}
                  tooltip={option['tooltip']}
                >
                  <SwitchElementUpdated
                    onSwitch={() => onTogglePaymentOption(option['key'])}
                    on={watch('paymentOption') === option['key']}
                  />
                </EventPaymentRow>
              )
            })}
            <hr className="my-4" />
            <EventPaymentRow
              title="Payment Methods"
              subtitle="Select one or more payment methods for this event"
            />
            <EventPaymentRow titleClassName="text-lg font-normal" title="Credit card payments">
              <SwitchElementUpdated
                onSwitch={() => handleToggleClick('allowCreditCardPayment')}
                on={watch('allowCreditCardPayment')}
              />
            </EventPaymentRow>
            <EventPaymentRow titleClassName="text-lg font-normal" title="Debit card and ACH wires">
              <SwitchElementUpdated
                onSwitch={() => handleToggleClick('allowACHPayment')}
                on={watch('allowACHPayment')}
              />
            </EventPaymentRow>
            <EventPaymentRow
              titleClassName="text-lg font-normal"
              title="Cash, Check, PayPal and Venmo Payments"
              subtitle="These payments will be collected outside of Pegasus, but we will help you record the payments for accounting purposes"
            >
              <SwitchElementUpdated
                onSwitch={() => handleToggleClick('allowWithoutPayment')}
                on={watch('allowWithoutPayment')}
              />
            </EventPaymentRow>
            <hr className="my-4" />
            <EventPaymentRow
              title="Delayed payments"
              subtitle="Register now, pay later by saving a payment method on file. Disable payment methods above to disable upfront payments."
            >
              <SwitchElementUpdated
                onSwitch={() => handleToggleClick('allowDelayedPayment')}
                on={watch('allowDelayedPayment')}
              />
            </EventPaymentRow>
            <EventPaymentRow
              title="Split payments"
              subtitle="Allow exhibitors to split the bill with team members. Team members will be registered immediately and sent a bill to pay later"
            >
              <SwitchElementUpdated
                onSwitch={() => handleToggleClick('allowSplitPayment')}
                on={watch('allowSplitPayment')}
              />
            </EventPaymentRow>
            <hr className="my-4" />
            <EventPaymentRow
              title="Collect Sales Tax"
              subtitle="Turn this on to collect sales tax on all entries, fees and tickets for this event"
              tooltip="You cannot change this after you publish your event."
            >
              <SwitchElementUpdated
                onSwitch={() => handleToggleClick('chargeSalesTax')}
                on={watch('chargeSalesTax')}
              />
            </EventPaymentRow>
          </div>
        </div>
      )}
    </EventFormContainerComponent>
  )
}

export default EventPaymentFormComponent
